import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { differenceInCalendarDays, subDays, format, isBefore, subYears, subMonths, subWeeks } from 'date-fns';
import { fi } from 'date-fns/locale';
import { cloneDeep } from 'lodash';
import styles from './styles/Consumption.module.scss';

import { OmaLummeApp, ConsumptionApp } from '../../omaLummeContext';

import Box from '../Common/Box';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import Tabs from '../Common/Tabs';
import ReportingGraph from '../Graphs/ReportingGraph/ReportingGraph';
import EliqGraph from '../Graphs/EliqGraph';
import { EliqHomeProfile } from './EliqHomeProfile';
import ConsumptionMeteringPointGroup from './MeteringPointGroup/ConsumptionMeteringPointGroup';
import ConsumptionMeteringPointGroupManage from './MeteringPointGroup/ConsumptionMeteringPointGroupManage';

// Actions.
import { fetchEliqUser } from '../../actions';
// Utils.
import { roundNumber, setPriceFormat } from '../../utils/billing';
import { capitalizeFirstLetter, graphDateStyle } from '../../utils/consumption';
import WeightedAverage from './WeightedAverage';

const Consumption: React.FunctionComponent = () => {
  const dispatchConsumption = ConsumptionApp.useDispatch();
  const { basicData, loadingBasicData, users, contracts, selectedMeteringPoint, selectedMeteringPointContract } = OmaLummeApp.useState();

  const { activeUser } = users;
  const customerType = activeUser.customerType;
  const { t } = useTranslation();

  const {
    activeFilter,
    dataLoading,
    startDate,
    endDate,
    homeProfile,
    homeProfileCompletion,
    euc,
    similarHomes,
    similarHomesConsumption,
    similarHomesReport,
  } = ConsumptionApp.useState();

  const { totalConsumption, previousConsumption, lastYearConsumption, consumptionEstimate } = ConsumptionApp.useState();

  const meteringPoint = basicData?.find(
    (mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId && mp.contractNo === selectedMeteringPoint.contractNo.toString(),
  );

  const productionContract =
    meteringPoint && meteringPoint.meteringpointType ? meteringPoint.meteringpointType.toLowerCase() === 'production' : false;

  const [eliqUserToken, setEliqUserToken] = React.useState('');
  const [activeB2BTab, setActiveB2BTab] = React.useState('single');
  const [activeTab, setActiveTab] = React.useState('Exove');

  const [showHomeProfile, setShowHomeProfile] = React.useState<boolean>(false);

  // This will check if the user is found in the Eliq side and use that graph then.
  React.useEffect(() => {
    async function checkEliqAvailability(): Promise<void> {
      const activeUserEliqToken = await fetchEliqUser(activeUser.userId, activeUser.userId);

      if (activeUserEliqToken) {
        setEliqUserToken(activeUserEliqToken);
      }
    }

    if (import.meta.env.VITE_ENV !== 'prod' && activeUser.userId !== 0) {
      setActiveTab('Exove');
      checkEliqAvailability();
    }
  }, [activeUser.userId]);

  React.useEffect(() => {
    if (activeB2BTab === 'single') {
      dispatchConsumption(['changeType', 'single']);
    } else {
      dispatchConsumption(['changeType', 'group']);
    }
  }, [activeB2BTab]);

  React.useEffect(() => {
    if (homeProfile && homeProfileCompletion === 0) {
      setShowHomeProfile(true);
    } else {
      setShowHomeProfile(false);
    }
  }, [homeProfile, homeProfileCompletion]);

  const SHC = similarHomesConsumption?.result.consumption
    ? similarHomesConsumption.result.consumption.reduce((acc: number, obj: number) => {
        return acc + obj;
      }, 0)
    : 0;

  const eliqComparisonValue =
    similarHomesReport && isBefore(new Date(similarHomesReport.result.to), new Date()) ? SHC : (similarHomesReport?.result.energy_wh ?? 0);

  const comparisonBars100 = similarHomesReport && eliqComparisonValue > totalConsumption * 1000 ? eliqComparisonValue : totalConsumption * 1000;

  const comparisonPrev = previousConsumption !== 0 ? (consumptionEstimate > 0 ? consumptionEstimate : totalConsumption) / previousConsumption : 0;
  const comparisonLastYear = lastYearConsumption !== 0 ? (consumptionEstimate > 0 ? consumptionEstimate : totalConsumption) / lastYearConsumption : 0;

  let dateFormat = 'yyyy';
  if (activeFilter === 'month') {
    dateFormat = 'LLLL yyyy';
  }

  let daySpan = 0;

  if (startDate && endDate && graphDateStyle(startDate, endDate, activeFilter) === 'custom') {
    const diff = differenceInCalendarDays(new Date(endDate), new Date(startDate));
    if (diff) {
      daySpan = diff + 1;
    }
  }

  const b2bTabs = [
    {
      label: t('consumption:meteringPointGroup.tabSingle'),
      key: 'single',
    },
    {
      label: t('consumption:meteringPointGroup.tabGroups'),
      key: 'groups',
    },
    {
      label: t('consumption:meteringPointGroup.tabManage'),
      key: 'manageGroups',
    },
  ];

  const consumptionTabs = [
    {
      label: t('consumption:consumption.consumptionGraphExove'),
      key: 'Exove',
    },
    {
      label: t('consumption:consumption.consumptionGraphEliq'),
      key: 'Eliq',
    },
  ];

  return (
    <>
      <Box>
        <h1 aria-live="polite">
          {t(
            selectedMeteringPointContract?.meteringPointDetails?.type.toLowerCase() === 'production'
              ? 'consumption:consumption.productionTitle'
              : 'consumption:consumption.consumptionTitle',
          )}
        </h1>

        {customerType === 'business' && (
          <div>
            <Tabs tabs={b2bTabs} reporting={true} active={{ activeTab: activeB2BTab, setActiveTab: setActiveB2BTab }} />
          </div>
        )}

        {activeB2BTab === 'groups' && <ConsumptionMeteringPointGroup />}
        {activeB2BTab === 'manageGroups' && <ConsumptionMeteringPointGroupManage />}

        {import.meta.env.VITE_ENV !== 'prod' && eliqUserToken !== '' && activeB2BTab === 'single' && (
          <Tabs tabs={consumptionTabs} active={{ activeTab, setActiveTab }} />
        )}

        {activeB2BTab === 'single' && activeTab === 'Exove' && activeUser && contracts && contracts.length > 0 && <ReportingGraph type="single" />}
        {activeB2BTab === 'single' && activeTab === 'Eliq' && <EliqGraph token={eliqUserToken} />}

        {!(dataLoading || loadingBasicData) && (!basicData || (basicData && basicData.length === 0)) && (
          <p>
            <strong>{t(`common:general.contractsOrMeteringPointsNotFound`)}</strong>
          </p>
        )}
      </Box>
      {(dataLoading || loadingBasicData) && <Loading />}
      {!dataLoading &&
        basicData &&
        basicData.length > 0 &&
        activeB2BTab === 'single' &&
        activeTab === 'Exove' &&
        activeUser &&
        !productionContract && (
          <>
            {comparisonPrev > 0 && activeFilter !== 'day' && (
              <Box>
                <h2 className="h1">{t('consumption:eliq.comparisonTitle')}</h2>
                <p className={styles.eliqComparisonIngress}>
                  <Trans
                    i18nKey="consumption:eliq.comparisonIngressPrevPeriod"
                    values={{
                      reaction: comparisonPrev < 1 ? t('consumption:eliq.comparisonIngressHooray') : '',
                      change: `${
                        comparisonPrev > 1 ? t('consumption:eliq.comparisonIngressUp') : t('consumption:eliq.comparisonIngressDown')
                      } ${roundNumber((1 - comparisonPrev) * -1 * 100, false, 0).replace('-', '')}`,
                      period: t(`consumption:eliq.comparisonIngress-${graphDateStyle(startDate, endDate, activeFilter)}`),
                    }}
                  >
                    .
                  </Trans>
                  {activeFilter !== 'year' && (
                    <>
                      <br />
                      <Trans
                        i18nKey="consumption:eliq.comparisonIngressThisPeriodLastYear"
                        values={{
                          change: `${
                            comparisonLastYear > 1 ? t('consumption:eliq.comparisonIngressUp') : t('consumption:eliq.comparisonIngressDown')
                          } ${roundNumber((1 - comparisonLastYear) * -1 * 100, false, 0).replace('-', '')}`,
                          lastYear: t(`consumption:eliq.comparisonIngressLastYear-${graphDateStyle(startDate, endDate, activeFilter)}`),
                        }}
                      >
                        .
                      </Trans>
                    </>
                  )}
                </p>
                <div className={`${styles.eliqConsumptionComparison} ${activeFilter === 'year' ? styles.year : styles.month}`}>
                  <div className={styles.eliqComparisonPrevious}>
                    <div>
                      <span className={`h1 ${styles.eliqComparisonAmount}`}>{roundNumber(previousConsumption, false, 0)} kWh</span>
                      <span>
                        {comparisonPrev > 1 ? '+' : ''}
                        {roundNumber((1 - comparisonPrev) * -1 * 100, false, 0)} %
                      </span>
                      {t(`consumption:eliq.previousConsumption-${graphDateStyle(startDate, endDate, activeFilter)}`)}
                    </div>

                    {startDate && endDate && (
                      <>
                        {graphDateStyle(startDate, endDate, activeFilter) === 'custom' ? (
                          <h4>
                            {format(subDays(startDate, daySpan), 'd.M.')}–{format(subDays(endDate, daySpan), 'd.M.yyyy')}
                          </h4>
                        ) : (
                          <h4>
                            {activeFilter === 'week'
                              ? t('consumption:eliq.previousWeek')
                              : capitalizeFirstLetter(
                                  format(
                                    activeFilter === 'month'
                                      ? subMonths(startDate, 1)
                                      : activeFilter === 'week'
                                        ? subWeeks(startDate, 1)
                                        : subYears(startDate, 1),
                                    dateFormat,
                                    { locale: fi },
                                  ),
                                )}
                          </h4>
                        )}
                      </>
                    )}
                  </div>
                  {activeFilter !== 'year' && (
                    <div className={styles.eliqComparisonLastYear}>
                      <div>
                        <span className={`h1 ${styles.eliqComparisonAmount}`}>{roundNumber(lastYearConsumption, false, 0)} kWh</span>
                        <span>
                          {comparisonLastYear > 1 ? '+' : ''}
                          {roundNumber((1 - comparisonLastYear) * -1 * 100, false, 0)} %
                        </span>
                        {t('consumption:eliq.previousConsumption-year')}
                      </div>

                      {graphDateStyle(startDate, endDate, activeFilter) === 'custom' ? (
                        <h4>
                          {format(startDate || new Date(), 'd.M.')}–{format(endDate || new Date(), 'd.M.yyyy')}
                        </h4>
                      ) : (
                        <h4>
                          {activeFilter === 'week'
                            ? t('consumption:eliq.previousYear')
                            : capitalizeFirstLetter(format(subYears(startDate || new Date(), 1), dateFormat, { locale: fi }) ?? '')}
                        </h4>
                      )}
                    </div>
                  )}
                  {consumptionEstimate > 0 && (
                    <div className={styles.eliqComparisonEstimate}>
                      <div>
                        <span className={`h1 ${styles.eliqComparisonAmount}`}>{roundNumber(consumptionEstimate, false, 0)} kWh</span>
                        <span>&nbsp;</span>
                        {t('consumption:eliq.forecast')}
                      </div>

                      <h4>
                        {activeFilter === 'week'
                          ? t('consumption:eliq.upcomingWeek')
                          : capitalizeFirstLetter(startDate ? format(startDate, dateFormat, { locale: fi }) : '')}
                      </h4>
                    </div>
                  )}
                  {endDate && isBefore(endDate, new Date()) && consumptionEstimate === 0 && (
                    <div className={styles.eliqComparisonPrevious}>
                      <div>
                        <span className={`h1 ${styles.eliqComparisonAmount}`}>{roundNumber(totalConsumption, false, 0)} kWh</span>
                        <span>&nbsp;</span>
                        {t('consumption:eliq.actual')}
                      </div>

                      {graphDateStyle(startDate, endDate, activeFilter) === 'custom' ? (
                        <h4>
                          {format(startDate || new Date(), 'd.M.')}–{format(endDate || new Date(), 'd.M.yyyy')}
                        </h4>
                      ) : (
                        <h4>
                          {activeFilter === 'week'
                            ? t('consumption:eliq.selectedWeek')
                            : capitalizeFirstLetter(startDate ? format(startDate, dateFormat, { locale: fi }) : '')}
                        </h4>
                      )}
                    </div>
                  )}
                </div>
              </Box>
            )}
            <WeightedAverage />
            {customerType === 'private' && (
              <>
                <div className={styles.eliqTwoCol}>
                  <Box size="half">
                    <h2 className="h1">{t('consumption:eliq.similarHomesTitle')}</h2>

                    {!['month', 'year'].includes(activeFilter) && <p>{t('consumption:eliq.energyUsageCategoriesSelectMonth')}</p>}
                    {['month', 'year'].includes(activeFilter) && (
                      <>
                        {!similarHomes && !similarHomesReport && <p>{t('consumption:eliq.similarHomesFillProfile')}</p>}
                        {dataLoading && <Loading size="small" />}
                        {!dataLoading && eliqComparisonValue > 0 && (
                          <>
                            <h5>{t('consumption:eliq.similarHomesYou')}</h5>
                            <div
                              className={`${styles.eliqSimilarHomesBar} ${styles.you}`}
                              style={{ width: `${((totalConsumption * 1000) / comparisonBars100) * 100}%` }}
                            >
                              {setPriceFormat(roundNumber(totalConsumption))} kWh
                            </div>

                            <h5>{t('consumption:eliq.similarHomesSimilars')}</h5>
                            <div
                              className={`${styles.eliqSimilarHomesBar} ${styles.similars}`}
                              style={{ width: `${(eliqComparisonValue / comparisonBars100) * 100}%` }}
                            >
                              {setPriceFormat(roundNumber(eliqComparisonValue / 1000))} kWh
                            </div>
                          </>
                        )}

                        {!dataLoading && similarHomes?.result_status?.code === 'ok' && (
                          <>
                            <h5>{t('consumption:eliq.similarHomesReference')}</h5>
                            <p className={styles.eliqSimilarHomesParagraph}>
                              {similarHomes.result.filters.map((filter) => {
                                let unit = '';
                                if (filter.key === 'living_area') {
                                  unit = t(`consumption:eliq.profile-living_area_unit`);
                                }
                                if (filter.key === 'persons') {
                                  unit = t(`consumption:eliq.profile-${filter.limit_range.max === 1 ? 'persons_1' : 'persons_multiple'}`);
                                }
                                return (
                                  <React.Fragment key={filter.key}>
                                    {filter.limit_values && t(`consumption:eliq.profile-${filter.limit_values[0]}`)}
                                    {filter.limit_range && (
                                      <>
                                        {filter.limit_range.min}
                                        {filter.limit_range.min !== filter.limit_range.max && <>&ndash;{filter.limit_range.max}</>} {unit}
                                      </>
                                    )}
                                    <br />
                                  </React.Fragment>
                                );
                              })}
                            </p>
                          </>
                        )}
                      </>
                    )}

                    <p>{t('consumption:eliq.energyUsageCategoriesDisclaimer')}</p>

                    <Button onClick={(): void => setShowHomeProfile(true)}>{t('consumption:eliq.updateHomeProfile')}</Button>
                  </Box>
                  <Box size="half">
                    <h2 className="h1">{t('consumption:eliq.energyUsageCategoriesTitle')}</h2>

                    {!euc && activeFilter !== 'month' && <p>{t('consumption:eliq.energyUsageCategoriesSelectMonth')}</p>}
                    {!euc && activeFilter === 'month' && <p>{t('consumption:eliq.similarHomesFillProfile')}</p>}
                    {euc?.breakdown && euc?.breakdown.length && (
                      <>
                        {euc.breakdown
                          .sort((a, b) => (a.value < b.value ? 1 : -1))
                          .map((cat) => {
                            const breakdown = cloneDeep(euc.breakdown);
                            const topValue = breakdown.sort((a, b) => (a.value < b.value ? 1 : -1))[0].value;

                            return (
                              <div key={cat.category} className={`${styles.eliqEuc} ${styles[cat.category]}`}>
                                <span className={styles.eucIcon} />
                                <span className={styles.eucTitleValue}>
                                  {t(`consumption:eliq.energyUsageCategories-${cat.category}`)}: {setPriceFormat(roundNumber(cat.value / 1000))} kWh
                                </span>
                                <div className={styles.eucBarPercentage}>
                                  <span className={styles.eucBar} style={{ width: `${(cat.value / topValue) * 100}%` }}></span>
                                  <span className={styles.eucPercentage}>{roundNumber(cat.percentage, true, 0)} %</span>
                                </div>
                              </div>
                            );
                          })}
                      </>
                    )}

                    <p>{t('consumption:eliq.energyUsageCategoriesDisclaimer')}</p>

                    <Button onClick={(): void => setShowHomeProfile(true)}>{t('consumption:eliq.updateHomeProfile')}</Button>
                  </Box>
                </div>

                <EliqHomeProfile open={showHomeProfile} setShowHomeProfile={setShowHomeProfile} />
              </>
            )}
          </>
        )}
      {euc && customerType === 'private' && !productionContract && (
        <p className="electrictCategoryExplanations">
          <Trans values={{ link: t(`consumption:consumption.consumptionElectricCategoryExplanationsLink`) }}>
            <a href="https://www.lumme-energia.fi/omalumme_energiankaytto" target="_blank" rel="noopener noreferrer"></a>
          </Trans>
        </p>
      )}
    </>
  );
};

export default Consumption;
